import {
  DataGridPremium,
  DataGridPremiumProps,
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridInitialState,
  GridRow,
  GridRowGroupingModel,
  GridRowId,
  GridRowParams,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import clsx from 'clsx'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { trySaveToLocalStorage } from '@omnea-app/fe-browser-utils/trySaveToLocalStorage'
import { isDefined } from '@omnea-app/legacy/utils/type-guard/isDefined'
import { omneaDataGridClasses } from '@omnea-app/omui/dataGridClasses'
import { CustomPagination } from './CustomPagination'
import { DataGridContainer } from './DataGridContainer'
import { DataGridLoading } from './DataGridLoading'
import { DefaultNoRowsState } from './DefaultNoRowsState'
import { useDataGridAnalytics } from './hooks/useDataGridAnalytics'
import { useHasScrolledHorizontally } from './hooks/useHasScrolledHorizontally'
import { useMuiDataGridLocaleText } from './hooks/useMuiDataGridLocaleText'
import { usePreserveScrollPosition } from './hooks/usePreserveScrollPosition'
import { useEditingRowsContext } from './RowEditingContext'
import { mergeColumnOrder } from './utils/mergeColumnOrder'

export type TableEditMode = 'row' | 'cell'

export interface DataGridBaseProps extends DataGridPremiumProps {
  stateKey?: string | null
  defaultSortModel?: {
    field: string
    sort: 'asc' | 'desc'
  }
  initialColumnVisibility?: GridColumnVisibilityModel
  getDefaultRowLink?: (id: GridRowId) => string | null
  hasUncontainedTableStyling?: boolean
  allowPageScroll?: boolean
  pinnedColumns?: {
    left?: string[]
    right?: string[]
  }
  editMode?: TableEditMode
  TableEmptyState?: () => ReactNode
  onRowClick?: (params: GridRowParams, event: MuiEvent<React.MouseEvent<HTMLElement>>, details: GridCallbackDetails) => void
  pageSizeOptions?: number[]
  hasExternalHeader?: boolean
  totalCountLoading?: boolean
  totalCountIsApprox?: boolean
}

export const DataGridBase = ({
  sx,
  stateKey,
  loading,
  defaultSortModel,
  initialColumnVisibility,
  slots,
  getDefaultRowLink,
  hasUncontainedTableStyling,
  allowPageScroll,
  pinnedColumns,
  hideFooter,
  apiRef: apiRefProp,
  editMode = 'row',
  TableEmptyState = DefaultNoRowsState,
  onRowClick,
  onFilterModelChange,
  filterModel,
  pageSizeOptions = [10, 25, 50, 100],
  hasExternalHeader = false,
  totalCountLoading = false,
  totalCountIsApprox = false,
  rowCount,
  getRowClassName,
  className: classNameProp,
  ...props
}: DataGridBaseProps) => {
  const localeText = useMuiDataGridLocaleText()
  const defaultApiRef = useGridApiRef()
  const apiRef = useState(apiRefProp ?? defaultApiRef)[0]
  const { currentlyEditingRow } = useEditingRowsContext()
  const pathname = usePathname()

  const localStorageKey = isDefined(stateKey) ? getTableStateLocalStorageKey(stateKey) : null
  const initialState = useMemo(() => getLocalStorageState(localStorageKey), [localStorageKey])

  // IF we have unstable datasource don't set the grouping model
  const [rowGroupingModel, setRowGroupingModel] = useState<GridRowGroupingModel>(
    props.unstable_dataSource ? [] : (initialState.rowGrouping?.model ?? [])
  )
  const sortModel = useMemo(() => (defaultSortModel ? [defaultSortModel] : undefined), [defaultSortModel])

  // Order columns based on stored table state
  const orderedColumns = useMemo(() => {
    const tableState = getLocalStorageState(localStorageKey)
    const orderedFields = tableState.columns?.orderedFields
    if (!isArray(orderedFields) || props.disableColumnReorder) {
      return props.columns
    }

    // NOTE (Dalyan): We need to ensure that the persisted column widths from localStorage
    // are used for the returned `orderingColumns` value - otherwise, it will set the column width
    // to the default value defined by us in the column config, or the MUI default of `100`
    const columnsWithWidth = props.columns.map((column) => {
      const width = (tableState.columns?.dimensions ?? {})[column.field]?.width
      return { ...column, ...(isDefined(width) && { width }) }
    })

    return mergeColumnOrder(columnsWithWidth, orderedFields)
  }, [props.columns, localStorageKey, props.disableColumnReorder])

  const handleStateChange = useCallback(() => {
    if (isDefined(localStorageKey)) {
      const exportedState = apiRef?.current?.exportState()
      if (isDefined(exportedState)) {
        trySaveToLocalStorage({ key: localStorageKey, value: JSON.stringify(exportedState) })
      }
    }
  }, [apiRef, localStorageKey])

  const hasScrolledHorizontally = useHasScrolledHorizontally(apiRef)
  const hasFilterPanel = isDefined(slots?.filterPanel)

  const { trackRowClick, trackFilterChange } = useDataGridAnalytics({
    loading: !!loading,
    rows: Array.from(props.rows ?? []),
    tableName: isDefined(stateKey) ? stateKey : (pathname ?? 'unknown_table'),
    filterModel,
    sortModel,
    initialState,
    rowGroupingModel,
  })

  // Check if we're using V2 data source pattern
  const isUsingDataSource = !!props.unstable_dataSource

  // Preserve scroll position across re-renders
  usePreserveScrollPosition({
    apiRef,
    isUsingDataSource,
    rows: props.rows ?? [],
    stateKey,
  })

  const handleRowClick = (
    params: GridRowParams,
    event: MuiEvent<React.MouseEvent<HTMLElement>>,
    details: GridCallbackDetails
  ) => {
    trackRowClick()
    if (onRowClick) {
      onRowClick(params, event, details)
    }
  }

  const handleFilterModelChange = (updatedFilterModel: GridFilterModel, details: GridCallbackDetails) => {
    trackFilterChange(updatedFilterModel, details)
    if (onFilterModelChange) {
      onFilterModelChange(updatedFilterModel, details)
    }
  }

  const mergedGetRowClassName = useCallback(
    (params: Parameters<NonNullable<DataGridPremiumProps['getRowClassName']>>[0]) =>
      clsx(String(params.id).includes('auto-generated-row') && 'grouping-row', getRowClassName?.(params)),
    [getRowClassName]
  )

  const roundTopCorners = !(hasFilterPanel || hasExternalHeader)

  return (
    <DataGridContainer>
      {loading && props.rows?.length === 0 ? (
        <DataGridLoading hasUncontainedTableStyling={hasUncontainedTableStyling} />
      ) : (
        <DataGridPremium
          editMode={editMode}
          getRowClassName={mergedGetRowClassName}
          onProcessRowUpdateError={(error) => console.error('Failed to process row update', { error })}
          pagination
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          {...(!isUsingDataSource && { onFilterModelChange: handleFilterModelChange })}
          loading={loading}
          columnHeaderHeight={40}
          pageSizeOptions={pageSizeOptions}
          filterDebounceMs={500}
          // Modifier classes consumed by the omui MuiDataGrid theme override
          // (see apps/frontend/shared/omui/src/theme/modules/components/MuiDataGrid.ts)
          // to opt into the rounded-corner rules. classNameProp is merged here
          // because the V1 `{...props}` spread below would otherwise overwrite
          // our modifier classes if a consumer passes className.
          className={clsx(
            roundTopCorners && omneaDataGridClasses.roundTop,
            !roundTopCorners && omneaDataGridClasses.roundPinnedLeftHeaderTop,
            !hasFilterPanel && omneaDataGridClasses.roundHeaderRowTop,
            !hasExternalHeader && omneaDataGridClasses.roundFirstChildTop,
            hideFooter && omneaDataGridClasses.roundBottom,
            allowPageScroll && omneaDataGridClasses.allowPageScroll,
            classNameProp
          )}
          sx={{
            // Vast majority of the styling here should be part of the Omui theme override, NOT here!
            // Only bits which depend on props
            '& .MuiDataGrid-virtualScroller': {
              overscrollBehavior: allowPageScroll ? 'auto' : 'none', // Prevents scroll overshoot/scroll chaining when allowPageScroll is false; uses default scrolling when true
              // Disable vertical scrolling when table is empty to allow page scroll (preserve horizontal for column headers)
              ...(props.rows && props.rows.length === 0 && { overflowY: 'hidden !important' }),
            },
            ...sx,
            '& .MuiDataGrid-cell--pinnedLeft': {
              boxShadow: hasScrolledHorizontally ? '8px 0px 15px -8px #D1D1D1' : 'none',
            },
          }}
          onStateChange={handleStateChange}
          showColumnVerticalBorder
          localeText={localeText}
          slots={{
            noRowsOverlay: isNil(currentlyEditingRow) ? TableEmptyState : () => null,
            noResultsOverlay: isNil(currentlyEditingRow) ? TableEmptyState : () => null,
            row: (rowProps) => {
              const isGroupedRowDropdownMenu = String(rowProps.rowId).match(/^auto-generated-row/)
              if (isGroupedRowDropdownMenu) return <GridRow {...rowProps} />

              const rowLink = getDefaultRowLink?.(rowProps.rowId)

              if (typeof rowLink === 'string' && currentlyEditingRow !== rowProps.rowId.toString()) {
                return (
                  <Link href={rowLink}>
                    <GridRow {...rowProps} />
                  </Link>
                )
              }

              if (typeof rowLink !== 'string') return <GridRow {...rowProps} />
              return (
                <Link href={rowLink}>
                  <GridRow {...rowProps} />
                </Link>
              )
            },
            ...((totalCountLoading || totalCountIsApprox || isUsingDataSource) && {
              pagination: () => (
                <CustomPagination totalCountLoading={totalCountLoading} totalCountIsApprox={totalCountIsApprox} />
              ),
            }),
            ...slots,
          }}
          initialState={{
            sorting: {
              sortModel,
            },
            ...initialState,
            ...(pinnedColumns && { pinnedColumns }),
          }}
          columnVisibilityModel={initialColumnVisibility}
          pinnedColumns={rowGroupingModel.length === 0 ? pinnedColumns : undefined}
          groupingColDef={{ leafField: pinnedColumns?.left?.[0] }}
          hideFooter={hideFooter}
          {...(isUsingDataSource
            ? {
                unstable_dataSource: props.unstable_dataSource,
                slotProps: props.slotProps,
                ...(rowCount != null && { rowCount: totalCountIsApprox ? -1 : rowCount }),
                disableAggregation: props.disableAggregation,
                filterModel,
                onFilterModelChange: handleFilterModelChange,
                sortModel: props.sortModel,
                onSortModelChange: props.onSortModelChange,
                processRowUpdate: props.processRowUpdate,
                paginationModel: props.paginationModel,
                onPaginationModelChange: props.onPaginationModelChange,
                columnVisibilityModel: props.columnVisibilityModel,
                onColumnVisibilityModelChange: props.onColumnVisibilityModelChange,
                unstable_dataSourceCache: null,
                checkboxSelection: props.checkboxSelection,
                rowSelectionModel: props.rowSelectionModel,
                onRowSelectionModelChange: props.onRowSelectionModelChange,
                getRowHeight: props.getRowHeight,
              }
            : {
                ...props,
                filterModel,
                ...(rowCount != null && { rowCount: totalCountIsApprox ? -1 : rowCount }),
              })}
          apiRef={apiRef}
          columns={orderedColumns}
          rowGroupingModel={rowGroupingModel}
          onRowGroupingModelChange={(model) => setRowGroupingModel(model)}
        />
      )}
    </DataGridContainer>
  )
}

function getLocalStorageState(key: string | null): GridInitialState {
  try {
    const stateFromLocalStorage = isDefined(key) ? localStorage.getItem(key) : null
    return isDefined(stateFromLocalStorage) ? JSON.parse(stateFromLocalStorage) : {}
  } catch (error) {
    console.error('Error getting table state from local storage', error)
    return {}
  }
}

export function getTableStateLocalStorageKey(name: string): string {
  return `${name}_TABLE_STATE`
}
